import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Store } from '@ngxs/store';
import { filter, take } from 'rxjs';
import { UserSelectors } from '../../states/user/user-selector';
import { PackagePurchasesService } from '../../services/payments/package-purchases/package-purchases.service';
import { PackagesService } from '../../services/payments/packages/packages.service';
import { SuccessfulPurchaseRequest } from '../../models/payment/successfulPurchaseRequest';
import { SuccessfulPurchaseResponse } from '../../models/payment/successfulPurchaseResponse';

interface PackageOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-operations-students',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    CalendarModule,
    TableModule,
    ToastModule,
    TooltipModule
  ],
  providers: [MessageService],
  templateUrl: './operations-students.component.html',
  styleUrl: './operations-students.component.scss'
})
export class OperationsStudentsComponent implements OnInit {
  filterForm!: FormGroup;
  students: SuccessfulPurchaseResponse[] = [];
  packages: PackageOption[] = [];

  isLoading = false;
  showFilters = true;

  totalRecords = 0;
  first = 0;
  rowSize = 10;

  private counsellorId: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private store: Store,
    private packagePurchasesService: PackagePurchasesService,
    private packagesService: PackagesService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.loadPackages();
    this.resolveCounsellorAndLoad();
  }

  initializeForm(): void {
    this.filterForm = this.formBuilder.group({
      studentName: [null],
      studentEmail: [null],
      packageId: [null],
      dateRange: [null]
    });
  }

  private resolveCounsellorAndLoad(): void {
    this.store.select(UserSelectors.getUser).pipe(
      filter(user => !!user && !!user.id),
      take(1)
    ).subscribe(user => {
      this.counsellorId = user!.id;
      this.loadStudents(1);
    });
  }

  loadPackages(): void {
    this.packagesService.getAllPackages({
      name: '',
      servicesIncluded: [''],
      duration: 0,
      amount: 0,
      description: ''
    }).subscribe({
      next: (data) => {
        const arr = Array.isArray(data) ? data : (data as any)?.result ?? [];
        this.packages = arr.map((item: any) => ({
          id: item.Id ?? item.id ?? 0,
          name: item.Name ?? item.name ?? ''
        }));
        this.mapPackageNames();
      },
      error: (err) => console.error('Error loading packages:', err)
    });
  }

  buildRequest(pageNumber: number): SuccessfulPurchaseRequest {
    const formValue = this.filterForm.value;
    const dateRange = formValue.dateRange as Date[] | null;

    return {
      studentName: formValue.studentName || null,
      studentEmail: formValue.studentEmail || null,
      packageId: formValue.packageId || null,
      counsellorId: this.counsellorId,
      dateFrom: dateRange && dateRange[0] ? this.formatDate(dateRange[0]) : null,
      dateTo: dateRange && dateRange[1] ? this.formatDate(dateRange[1]) : null,
      pageNumber: pageNumber,
      pageSize: this.rowSize
    };
  }

  private formatDate(date: Date): string | null {
    if (!date) return null;
    try {
      return new Date(date).toISOString();
    } catch {
      return null;
    }
  }

  loadStudents(pageNumber: number): void {
    if (!this.counsellorId) {
      return;
    }
    if (pageNumber === 1) {
      this.first = 0;
    }
    this.isLoading = true;
    this.packagePurchasesService.listPackagePurchases(this.buildRequest(pageNumber)).subscribe({
      next: (response) => {
        this.students = response?.result ?? [];
        this.totalRecords = response?.total ?? 0;
        this.mapPackageNames();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load assigned students' });
      }
    });
  }

  private mapPackageNames(): void {
    if (this.students && this.students.length > 0) {
      this.students.forEach((student: any) => {
        if (student.purchasedPackages && student.purchasedPackages.length > 0) {
          student.packageName = student.purchasedPackages.map((p: any) => p.packageName).join(', ');
        }
      });
    }
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.rowSize = event.rows ?? this.rowSize;
    const pageNumber = Math.floor(this.first / this.rowSize) + 1;
    this.loadStudents(pageNumber);
  }

  onFilter(): void {
    this.loadStudents(1);
  }

  clearAllFilters(): void {
    this.filterForm.reset({
      studentName: null,
      studentEmail: null,
      packageId: null,
      dateRange: null
    });
    this.onFilter();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  viewDocuments(studentId: string): void {
    this.router.navigate(['operations-portal', 'student', studentId, 'details'], { queryParams: { tab: 'documents' } });
  }

  viewProfile(studentId: string): void {
    this.router.navigate(['operations-portal', 'student', studentId, 'details'], { queryParams: { tab: 'profile' } });
  }

  viewApplications(studentId: string): void {
    this.router.navigate(['operations-portal', 'student', studentId, 'details'], { queryParams: { tab: 'applications' } });
  }
}
